import { Button, Select, Space, Tag, Typography } from "antd";
import type { ColumnsType } from "antd/es/table";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { DataTable } from "@/shared/components/DataTable";
import { useDebouncedValue } from "@/shared/hooks/useDebouncedValue";
import { useEmployeeNames } from "@/shared/hooks/useEmployeeNames";
import { usePermission } from "@/shared/hooks/usePermission";
import { AbsenceCreateModal } from "../components/AbsenceCreateModal";
import { AbsenceDetailDrawer } from "../components/AbsenceDetailDrawer";
import { useAbsenceEmployeeOptions, useAbsences } from "../hooks/useAbsences";
import {
	ABSENCE_STATUS_COLORS,
	ABSENCE_STATUS_VALUES,
	ABSENCE_TYPE_VALUES,
	type Absence,
	type AbsenceStatus,
	type AbsenceType,
} from "../types";

const { Title } = Typography;

export function AbsencesPage() {
	const { t } = useTranslation("absences");
	const { can } = usePermission();
	const [status, setStatus] = useState<AbsenceStatus | undefined>(undefined);
	const [type, setType] = useState<AbsenceType | undefined>(undefined);
	const [ochiq, setOchiq] = useState<Absence | null>(null);
	const [modalOchiq, setModalOchiq] = useState(false);
	const [xodimQidiruv, setXodimQidiruv] = useState("");
	const debounced = useDebouncedValue(xodimQidiruv, 300);

	const query = useAbsences({ status, type });
	const { data: xodimlar = [] } = useAbsenceEmployeeOptions(debounced);

	// ⚠️ Ismlar KO'RSATILAYOTGAN QATORLARDAN olinadi, filtr Select'ining
	// opsiya ro'yxatidan EMAS. Bu regressiya loyihada allaqachon bir
	// marta tuzatilgan (`ShiftInstancesPage.tsx:43`, `useEmployeeNames`
	// JSDoc'i): opsiyalar `limit: 100` va `q` bilan filtrlangan, ya'ni
	// qidiruvga bitta harf yozilishi bilan mavjud qatorlar ismdan xom
	// UUID ga aylanardi.
	const employeeNames = useEmployeeNames(query.items.map((row) => row.employeeId));

	const columns: ColumnsType<Absence> = [
		{
			title: t("columns.employee"),
			dataIndex: "employeeId",
			key: "employeeId",
			ellipsis: true,
			render: (_: unknown, row: Absence) => employeeNames.get(row.employeeId) ?? row.employeeId,
		},
		{
			title: t("columns.type"),
			dataIndex: "type",
			key: "type",
			render: (value: AbsenceType) => t(`type.${value}`),
		},
		{
			title: t("columns.range"),
			key: "range",
			render: (_: unknown, row: Absence) => `${row.dateFrom} — ${row.dateTo}`,
		},
		{
			title: t("columns.status"),
			dataIndex: "status",
			key: "status",
			render: (value: AbsenceStatus) => (
				<Tag color={ABSENCE_STATUS_COLORS[value]}>{t(`status.${value}`)}</Tag>
			),
		},
		{
			title: "",
			key: "actions",
			render: (_: unknown, row: Absence) => (
				<Button type="link" onClick={() => setOchiq(row)}>
					{t("page.open")}
				</Button>
			),
		},
	];

	return (
		<Space direction="vertical" style={{ width: "100%" }} size="middle">
			<Space style={{ justifyContent: "space-between", width: "100%" }}>
				<Title level={3} style={{ margin: 0 }}>
					{t("page.title")}
				</Title>
				{can("absences", "create") ? (
					<Button type="primary" onClick={() => setModalOchiq(true)}>
						{t("page.create")}
					</Button>
				) : null}
			</Space>

			<Space wrap>
				<Select
					allowClear
					placeholder={t("filters.statusPlaceholder")}
					style={{ minWidth: 180 }}
					value={status}
					onChange={setStatus}
					options={ABSENCE_STATUS_VALUES.map((value) => ({
						value,
						label: t(`status.${value}`),
					}))}
				/>
				<Select
					allowClear
					placeholder={t("filters.typePlaceholder")}
					style={{ minWidth: 180 }}
					value={type}
					onChange={setType}
					options={ABSENCE_TYPE_VALUES.map((value) => ({
						value,
						label: t(`type.${value}`),
					}))}
				/>
			</Space>

			<DataTable<Absence> rowKey="id" columns={columns} query={query} />

			<AbsenceCreateModal
				open={modalOchiq}
				onClose={() => setModalOchiq(false)}
				employeeOptions={xodimlar}
				onEmployeeSearch={setXodimQidiruv}
			/>
			<AbsenceDetailDrawer absence={ochiq} onClose={() => setOchiq(null)} />
		</Space>
	);
}
